// import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { basePageWrap } from '../BasePage';
import s from './PoiPage.module.css';
import ContentBlocks from '../../components/blocks/ContentBlocks';
import {
    WbButton,
    WbNotification,
} from '@digital-engineering-experiences/wb-webcomponents';
import ResponseDialog from '@/components/ResponseDialog/ResponseDialog';
import { getResponses, postResponse } from '@/api/responses';
import ResponseList from '@/components/Responses/ResponseList';
import { PoiPageProps } from '@/types/containers/poiPage';
import { ResponseProps } from '@/types/components/responseList';
import { ResponseToSubmit } from '@/types/components/responseDialog';
import { GeoLocation } from '@/types/components/responseDialog';
import ReactionPanelTooltip from '@/components/EsriMap/ReactionPanelTooltip';

const PoiPage = ({
    title,
    id,
    poitype,
    zoom,
    location,
    portal,
    coordinates,
    showReactionForm,
    showReactionList,
    keepReactionsVisible,
    reactionPanelTitle,
    reactionPanelContent,
    backButtonTitle,
    content,
    siteSetting,
    pageType,
}: PoiPageProps) => {
    const DynamicEsriMap = dynamic(
        () => import('../../components/EsriMap/EsriMap'),
        {
            ssr: false,
        }
    );
    const DynamicEsriScene = dynamic(
        () => import('../../components/EsriScene/EsriScene'),
        {
            ssr: false,
        }
    );

    const [feedbackModal, setFeedbackModal] = useState<boolean>(false);
    const [feedback, setFeedback] = useState<{ header: string; body: string }>({
        header: '',
        body: '',
    });
    const [, setSelectedGeolocation] = useState<GeoLocation | null>(null);
    const responseDialogRef = useRef(null);
    const poi = {
        title: title,
        id: id,
        coordinates: coordinates,
        poitype: poitype,
    };

    const sridIndex = location.indexOf('SRID=') + 5; // Index of the start of SRID value
    const semicolonIndex = location.indexOf(';', sridIndex); // Index of the semicolon after SRID value
    const sridValue = location.substring(sridIndex, semicolonIndex);
    const locationcoordinates = {
        x: coordinates[0],
        y: coordinates[1],
        spatialReference: {
            wkid: sridValue,
        },
    };
    const [responses, setResponses] = useState<ResponseProps[]>([]);

    const portalHref = portal.navigation.find(
        (item) => item.title === portal.title
    );

    const fetchResponses = (id: string) => {
        return new Promise((resolve) => {
            const fetchedResponses = getResponses(id);
            resolve(fetchedResponses);
        });
    };

    async function submitResponse(response: ResponseToSubmit) {
        if (portal.reactionForm) {
            try {
                await postResponse(portal.reactionForm.id, response);

                setFeedback({
                    header: portal.reactionForm.thankYouTitle,
                    body: portal.reactionForm.thankYouText,
                });
            } catch (error) {
                setFeedback({
                    header: 'Er is iets mis gegaan met het plaatsen van uw reactie. Probeer het later opnieuw. ',
                    body: '',
                });
            }
        }
        setFeedbackModal(true);
    }

    useEffect(() => {
        const formId = portal.reactionForm?.id.toString();
        if (formId) {
            if (showReactionList) {
                if (keepReactionsVisible || portal.reactionForm?.isActive) {
                    fetchResponses(formId).then((responsesResult) => {
                        setResponses(responsesResult as ResponseProps[]);
                    });
                }
            }
        }
    }, []);

    useEffect(() => {
        if (feedbackModal) {
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    //if esc key was not pressed in combination with ctrl or alt or shift
                    setFeedbackModal(false);
                }
            });
        }
    }, [feedbackModal]);

    return (
        <div className={s['PoiPage']} key={id}>
            <div className={s['PoiPage__map']}>
                <div className={s['PoiPage__esrimap']}>
                    {portal.pageType === 'PortalPage3d' ? (
                        <DynamicEsriScene
                            zoom={zoom ? zoom : 8}
                            host={portal.host}
                            center={coordinates}
                            mapId={portal.portalId}
                            pois={[poi]}
                            siteSetting={portal.siteSetting}
                            pageId={id}
                            pageType={pageType}
                        />
                    ) : (
                        <DynamicEsriMap
                            zoom={zoom ? zoom : 8}
                            minZoom={0}
                            maxZoom={23}
                            host={portal.host}
                            center={coordinates}
                            mapId={portal.portalId}
                            pois={[poi]}
                            siteSetting={portal.siteSetting}
                            pageId={id}
                            is3d={portal.is3d}
                            pageType={pageType}
                        />
                    )}
                </div>
                <div className={s['PoiPage__map__back']}>
                    <WbButton
                        iconLeft
                        iconType="chevron_left"
                        href={portalHref ? portalHref.url : '/'}
                        label={`${backButtonTitle} ${portal.title}`}></WbButton>
                </div>
            </div>
            {feedbackModal && (
                <>
                    <WbNotification
                        active={0}
                        close
                        classname={s['webmap__successnotification']}>
                        <>{feedback.body}</>
                    </WbNotification>
                </>
            )}
            <ContentBlocks content={content} />

            {responses.filter((response) => response.pageId === id).length >
                0 && (
                <div className="wb-container">
                    <div className={s['PoiPage__responseList']}>
                        <div className={s.PoiPage__responseList__header}>
                            <h2>{reactionPanelTitle}</h2>
                            {reactionPanelContent && (
                                <div className={s.PoiPage__tooltipwrapper}>
                                    <ReactionPanelTooltip
                                        reactionPanelContent={
                                            reactionPanelContent
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        <ResponseList
                            responses={responses.filter(
                                (response) => response.pageId === id
                            )}
                            pageId={id}
                            interactive={false}
                        />
                    </div>
                </div>
            )}

            {showReactionForm &&
                portal.reactionForm &&
                portal.reactionForm.isActive && (
                    <>
                        <ResponseDialog
                            dialogReference={responseDialogRef}
                            respondForm={portal.reactionForm}
                            siteSetting={siteSetting}
                            host={portal.host}
                            portalId={portal.portalId}
                            locationcoordinates={locationcoordinates}
                            resetGeolocation={setSelectedGeolocation}
                            setSelectedGeolocation={setSelectedGeolocation}
                            responseDialogOpenedByButton={false}
                            onSubmit={submitResponse}
                            pageId={id}
                            portalPage={false}
                            searchBoxEnabled={false}
                        />
                        <div className={s['PoiPage__map__participate__footer']}>
                            <div className={s['PoiPage__map__participate']}>
                                <WbButton
                                    label={portal.reactionForm.buttonText}
                                    iconType="comment"
                                    size="large"
                                    onClick={() => {
                                        if (
                                            (responseDialogRef.current as any)
                                                ?.showModal
                                        ) {
                                            (
                                                responseDialogRef.current as any
                                            ).showModal();
                                        }
                                    }}></WbButton>
                            </div>
                        </div>
                    </>
                )}
        </div>
    );
};

export default basePageWrap(PoiPage);
